import { Suspense, lazy } from "react";
import { Navigate, useRoutes, useLocation } from "react-router-dom";

import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
// guards
import GuestGuard from "../guards/GuestGuard";
import AuthGuard from "../guards/AuthGuard";

// components
import LoadingScreen from "../components/LoadingScreen";
import { PATH_PAGE } from "./paths";
import DashboardLayout from "../layouts/dashboard";
import WorkInProgress from "../../src/pages/WorkInProgress"; // Update the path
import PermissionDenied from "../pages/PermissionDenied";
import AccessBasedGuard from "../guards/AccessBasedGuard";
import MissingTimesheets from "../sections/TimeSheet/MissingTimesheets";
import DashBoardCustomView from "../sections/DashBoard/DashBoardView";
import { accessibleFeatures } from "../utils/rolesAccessMapping";
import { BudgetList } from "../pages/BudgetList";
//----------------------------------------------------------------------
const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense
      fallback={
        <LoadingScreen isDashboard={pathname.includes(PATH_PAGE.dashboard)} />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    { path: "/", element: <Navigate to={PATH_PAGE.login} replace /> },
    {
      path: PATH_PAGE.permissionDenied,
      element: <PermissionDenied />
    },
    {
      path: "",
      element: (
        <GuestGuard>
          <LogoOnlyLayout />
        </GuestGuard>
      ),
      children: [{ path: PATH_PAGE.login, element: <Login /> }]
    },

    // Dashboard Routes
    {
      path: "",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: PATH_PAGE.dashboard,
          element: (
            <AccessBasedGuard
              allowedRoles={accessibleFeatures.accessBasedGuard.clientDetails}
            >
              <Dashboard />
            </AccessBasedGuard>
          )
        },
        {
          path: PATH_PAGE.budget,
          element: (
            <AccessBasedGuard
              allowedRoles={accessibleFeatures.accessBasedGuard.clientDetails}
            >
              <BudgetList />
            </AccessBasedGuard>
          )
        },
        {
          path: PATH_PAGE.rateCard.root,
          element: (
            <AccessBasedGuard
              allowedRoles={accessibleFeatures.accessBasedGuard.rateCard}
            >
              <RateCardPage />
            </AccessBasedGuard>
          )
        },
        {
          path: PATH_PAGE.rateCard.details,
          element: (
            <AccessBasedGuard
              allowedRoles={accessibleFeatures.accessBasedGuard.rateCard}
            >
              <RateCardDetails />
            </AccessBasedGuard>
          )
        },
        {
          path: PATH_PAGE.dashboardPage.root,
          element: (
            <AccessBasedGuard
              allowedRoles={accessibleFeatures.accessBasedGuard.dashboardPage}
            >
              <DashBoardCustomView />
            </AccessBasedGuard>
          )
        },
        { path: PATH_PAGE.pageRoles, element: <WorkInProgress /> },
        { path: PATH_PAGE.pageSettings, element: <WorkInProgress /> },
        { path: PATH_PAGE.pageInvoicing, element: <WorkInProgress /> },
        {
          path: "",
          element: <Clients />,
          children: [
            {
              path: PATH_PAGE.client.detail,
              element: (
                <AccessBasedGuard
                  allowedRoles={
                    accessibleFeatures.accessBasedGuard.clientDetails
                  }
                >
                  <ClientDetail />
                </AccessBasedGuard>
              )
            },
            {
              path: "",
              element: <Estimations />,
              children: [
                {
                  path: PATH_PAGE.estimation.root,
                  element: (
                    <AccessBasedGuard
                      allowedRoles={
                        accessibleFeatures.accessBasedGuard.estimation
                      }
                    >
                      <EstimationList />
                    </AccessBasedGuard>
                  )
                },
                {
                  path: PATH_PAGE.estimation.detail,
                  element: <EstimationDetail />
                },
                {
                  path: PATH_PAGE.estimation.pricing,
                  element: (
                    <AccessBasedGuard
                      allowedRoles={
                        accessibleFeatures.accessBasedGuard.pricingEstimation
                      }
                    >
                      <Pricing />
                    </AccessBasedGuard>
                  )
                },
                {
                  path: PATH_PAGE.estimation.pricingDetail,
                  element: <PricingDetail />
                }
              ]
            },
            {
              path: "",
              element: <Contracts />,
              children: [
                {
                  path: PATH_PAGE.contracts.root,
                  element: (
                    <AccessBasedGuard
                      allowedRoles={
                        accessibleFeatures.accessBasedGuard.contracts
                      }
                    >
                      <ContractList />
                    </AccessBasedGuard>
                  )
                },
                {
                  path: PATH_PAGE.contracts.detail,
                  element: <ContractDetail />
                },
                {
                  path: PATH_PAGE.contracts.milestones,
                  element: (
                    <AccessBasedGuard
                      allowedRoles={
                        accessibleFeatures.accessBasedGuard.contractMilestones
                      }
                    >
                      <MilestonesList />
                    </AccessBasedGuard>
                  )
                },
                {
                  path: PATH_PAGE.contracts.milestoneDetail,
                  element: <MilestoneDetail />
                },
                {
                  path: PATH_PAGE.contracts.purchaseOrder,
                  element: (
                    <AccessBasedGuard
                      allowedRoles={
                        accessibleFeatures.accessBasedGuard
                          .contractPurchaseOrder
                      }
                    >
                      <PurchaseOrderList />
                    </AccessBasedGuard>
                  )
                },
                {
                  path: PATH_PAGE.contracts.purchaseOrderDetail,
                  element: <PurchaseOrderDetail />
                }
              ]
            },
            {
              path: PATH_PAGE.allocation.root,
              element: (
                <AccessBasedGuard
                  allowedRoles={accessibleFeatures.accessBasedGuard.allocation}
                >
                  <AllocationList />
                </AccessBasedGuard>
              )
            },
            {
              path: PATH_PAGE.allocation.detail,
              element: <AllocationDetail />
            },
            {
              path: PATH_PAGE.invoicing.root,
              element: (
                <AccessBasedGuard
                  allowedRoles={accessibleFeatures.accessBasedGuard.invoicing}
                >
                  <InvoicingList />
                </AccessBasedGuard>
              )
            },
            {
              path: PATH_PAGE.insights.root,
              element: (
                <AccessBasedGuard
                  allowedRoles={accessibleFeatures.accessBasedGuard.insights}
                >
                  <ClientInsights />
                </AccessBasedGuard>
              )
            },
            {
              path: "",
              element: <ResourcesManagement />,
              children: [
                {
                  path: PATH_PAGE.resourcesManagement.root,
                  element: (
                    <AccessBasedGuard
                      allowedRoles={
                        accessibleFeatures.accessBasedGuard.resourcesManagement
                      }
                    >
                      {" "}
                      <ResourceMetricsScreen />
                    </AccessBasedGuard>
                  )
                },
                {
                  path: PATH_PAGE.resourcesManagement.root,
                  element: (
                    <AccessBasedGuard
                      allowedRoles={
                        accessibleFeatures.accessBasedGuard.resourcesManagement
                      }
                    >
                      <ResourceInsights />
                    </AccessBasedGuard>
                  )
                },
                {
                  path: PATH_PAGE.resourcesManagement.consultants,
                  element: (
                    <AccessBasedGuard
                      allowedRoles={
                        accessibleFeatures.accessBasedGuard.resourcesManagement
                      }
                    >
                      <ConsultantsList />
                    </AccessBasedGuard>
                  )
                },
                {
                  path: PATH_PAGE.resourcesManagement.detail,
                  element: <WorkInProgress />
                },
                {
                  path: PATH_PAGE.resourcesManagement.utilization,
                  element: <ResourceUtilizationPage />
                },
                {
                  path: PATH_PAGE.resourcesManagement.insights,
                  element: <ResourceInsights />
                },
                {
                  path: PATH_PAGE.timesheetManagement.root,
                  element: <TimeSheetList />
                },
                {
                  path: PATH_PAGE.timesheetManagement.timesheetOverview,
                  element: <TimesheetOverview />
                },
                {
                  path: PATH_PAGE.timesheetManagement.empTimesheetOverview,
                  element: <EmpTimesheetOverview />
                },
                {
                  path: PATH_PAGE.contractList,
                  element: <ActiveContractList />
                },
                {
                  path: PATH_PAGE.timesheetManagement.managerView,
                  element: <ManagerView />
                },

                {
                  path: PATH_PAGE.timesheetManagement
                    .ongoinProjectTimesheetList,
                  element: <EmpTimesheetOngoingProjectList />
                },
                {
                  path: PATH_PAGE.timesheetManagement.missingTimesheets,
                  element: <MissingTimesheets />
                },
                {
                  path: PATH_PAGE.timesheetManagement.empTimesheetTotalHrs,
                  element: <EmpTimesheetTotalHrsCountList />
                }
              ]
            },
            {
              path: PATH_PAGE.exportTimesheetManagement.root,
              element: <ExportTimesheet />
            },
            {
              path: "",
              element: <SettingManagment />,
              children: [
                {
                  path: PATH_PAGE.setting.root,
                  element: (
                    <AccessBasedGuard
                      allowedRoles={accessibleFeatures.accessBasedGuard.setting}
                    >
                      <RateCardConfigPage />
                    </AccessBasedGuard>
                  )
                },
                {
                  path: PATH_PAGE.setting.skillPayrateConfigList,
                  element: <SkillPayRateConfigList />
                },

                {
                  path: PATH_PAGE.setting.skillPayrateDetails,
                  element: <SkillPayrateDetails />
                },
                {
                  path: PATH_PAGE.setting.customeraccess,
                  element: <CustomerAccessPage />
                },
                {
                  path: PATH_PAGE.setting.customerdeatil,
                  element: <CustomerDetail />
                },
                {
                  path: PATH_PAGE.setting.asset,
                  element: <AssetListing />
                },
                {
                  path: PATH_PAGE.asset.detail,
                  element: <AssetDetails />
                },
                {
                  path: PATH_PAGE.setting.employee,
                  element: (
                    <AccessBasedGuard
                      allowedRoles={
                        accessibleFeatures.accessBasedGuard.employeeSettings
                      }
                    >
                      {" "}
                      <EmployeePage />
                    </AccessBasedGuard>
                  )
                },
                {
                  path: PATH_PAGE.setting.employeeDetail,
                  element: <EmployeeDetail />
                },
                {
                  path: PATH_PAGE.setting.invoiceSettings,
                  element: (
                    <AccessBasedGuard
                      allowedRoles={
                        accessibleFeatures.accessBasedGuard.invoiceSettings
                      }
                    >
                      <InvoiceTemplatesList />
                    </AccessBasedGuard>
                  )
                }
              ]
            }
          ]
        }
      ]
    },

    { path: PATH_PAGE.page404, element: <Page404 /> },
    { path: "*", element: <Navigate to={PATH_PAGE.page404} replace /> }
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import("../pages/Login")));

const ClientDetail = Loadable(
  lazy(() => import("../sections/clients/ClientDetails"))
);

const Dashboard = Loadable(lazy(() => import("../pages/Dashboard")));
const ResourcesManagement = Loadable(
  lazy(() => import("../pages/ResourceManagement"))
);
const Clients = Loadable(lazy(() => import("../pages/Clients")));
const Estimations = Loadable(lazy(() => import("../pages/Estimations")));
const EstimationList = Loadable(
  lazy(() => import("../sections/Estimation/EstimationList"))
);

const EstimationDetail = Loadable(
  lazy(() => import("../sections/Estimation/EstimationPreview"))
);
const Contracts = Loadable(lazy(() => import("../pages/Contracts")));

const ContractDetail = Loadable(
  lazy(() => import("../sections/ContractManagement/ContractPreview"))
);
const MilestonesList = Loadable(
  lazy(() => import("../sections/ContractManagement/Milestones/MilestonesList"))
);
const MilestoneDetail = Loadable(
  lazy(() =>
    import("../sections/ContractManagement/Milestones/MilestoneDetail")
  )
);
const Pricing = Loadable(
  lazy(() => import("../sections/Estimation/Pricing/Pricing"))
);
const PricingDetail = Loadable(
  lazy(() => import("../sections/Estimation/Pricing/PricingDetail"))
);
const PurchaseOrderList = Loadable(
  lazy(() =>
    import("../sections/ContractManagement/PurchaseOrder/PurchaseOrderList")
  )
);
const PurchaseOrderDetail = Loadable(
  lazy(() =>
    import("../sections/ContractManagement/PurchaseOrder/PurchaseOrderDetail")
  )
);

const AllocationList = Loadable(
  lazy(() => import("../sections/Allocations/AllocationList"))
);

const AllocationDetail = Loadable(
  lazy(() => import("../sections/Allocations/AllocaionDetail"))
);

const InvoicingList = Loadable(
  lazy(() => import("../sections/Invoicing/InvoicesListing"))
);

const ClientInsights = Loadable(
  lazy(() => import("../sections/insights/ClientInsights"))
);

const TimeSheetList = Loadable(
  lazy(() => import("../sections/TimeSheet/TimeSheetListScreen"))
);

const TimesheetOverview = Loadable(
  lazy(() => import("../sections/TimeSheet/TimesheetOverview"))
);

const EmpTimesheetOverview = Loadable(
  lazy(() => import("../sections/TimeSheet/EmpTimeSheetOverviewPage"))
);

const ContractList = Loadable(
  lazy(() => import("../sections/ContractManagement/ContractList"))
);

const ActiveContractList = Loadable(
  lazy(() => import("../sections/DashBoard/ActiveContractList"))
);
const ManagerView = Loadable(
  lazy(() => import("../sections/TimeSheet/ManagerView"))
);

const EmpTimesheetOngoingProjectList = Loadable(
  lazy(() => import("../sections/TimeSheet/EmpOngoingProjectTimesheetList"))
);
const EmpTimesheetTotalHrsCountList = Loadable(
  lazy(() => import("../sections/TimeSheet/EmpTotalHrsPage"))
);

const ResourceMetricsScreen = Loadable(
  lazy(() => import("../sections/Resource/ResourceMetrics"))
);

const ResourceInsights = Loadable(
  lazy(() => import("../sections/Resource/ResourceInsights"))
);
const ConsultantsList = Loadable(
  lazy(() => import("../sections/Resource/ConsultantsList"))
);

const ExportTimesheet = Loadable(
  lazy(() => import("../sections/TimesheetExport/ExportTimesheet"))
);
const RateCardPage = Loadable(
  lazy(() => import("../sections/Estimation/RateCard/RateCard"))
);
const RateCardDetails = Loadable(
  lazy(() => import("../sections/SettingManagment/RateCardDetails"))
);
const RateCardConfigPage = Loadable(
  lazy(() => import("../sections/SettingManagment/RateConfigPage"))
);
const SkillPayRateConfigList = Loadable(
  lazy(() =>
    import(
      "../sections/SettingManagment/SkillPayrateConfig/SkillPayrateConfigList"
    )
  )
);
const SkillPayrateDetails = Loadable(
  lazy(() =>
    import(
      "../sections/SettingManagment/SkillPayrateConfig/SkillPayrateDetails"
    )
  )
);
const ResourceUtilizationPage = Loadable(
  lazy(() => import("../sections/Resource/ResourceUtilization"))
);
const CustomerAccessPage = Loadable(
  lazy(() => import("../sections/SettingManagment/CustomerAccessList"))
);
const CustomerDetail = Loadable(
  lazy(() => import("../sections/SettingManagment/CustomerDetail"))
);

const EmployeePage = Loadable(
  lazy(() => import("../sections/SettingManagment/EmployeeModule/EmployeeList"))
);
const AssetListing = Loadable(
  lazy(() =>
    import("../sections/SettingManagment/Asset Management/AssetListing")
  )
);

const AssetDetails = Loadable(
  lazy(() =>
    import("../sections/SettingManagment/Asset Management/AssetDetails")
  )
);

const EmployeeDetail = Loadable(
  lazy(() =>
    import("../sections/SettingManagment/EmployeeModule/EmployeeDetail")
  )
);
const SettingManagment = Loadable(lazy(() => import("../pages/Setting")));
const Page404 = Loadable(lazy(() => import("../pages/Page404")));
const InvoiceTemplatesList = Loadable(
  lazy(() => import("../pages/InvoiceTemplatesList"))
);
